import * as r from 'ramda';
import * as m from './model';

function productConfigurator(state) {
    return state.productConfigurator;
}

export function productConfig(state, id) {
    const pc = productConfigurator(state);
    const config = r.path(['productConfigs', id], pc);
    const category = m.category(state, config.categoryId);
    const selectedCategoryConfigs = r.pipe(
        r.pick(category.productConfigIds),
        r.values,
        r.reduce((sum, c) => sum + c.count, 0)
    )(pc.productConfigs);

    return r.pipe(
        r.assoc('lessAllowed', config.count > 0),
        r.assoc(
            'moreAllowed',
            selectedCategoryConfigs < category.maxConfigs ||
                (m.categoryShouldHaveExactly1Config(category) &&
                    config.count !== 1)
        )
    )(config);
}

const isAnyProductConfigSelected = r.any(r.propSatisfies(r.lt(0), 'count'));

/**
 * @returns {String} 'unrolled' | 'selected' | 'unselected'
 */
function categoryState(state, category) {
    if (category.unrolled) {
        return 'unrolled';
    }

    const productConfigs = r.map(
        r.partial(productConfig, [state]),
        category.productConfigIds
    );

    if (isAnyProductConfigSelected(productConfigs)) {
        return 'selected';
    }

    return 'unselected';
}

export function categoryConfig(state, id) {
    const pcState = productConfigurator(state);
    const category = pcState.categories[id];

    return r.pipe(
        r.assoc('unrolled', pcState.unrolledCategories.has(id)),
        (category) => r.assoc('state', categoryState(state, category), category)
    )(category);
}

export function categoryConfigs(state) {
    const pc = productConfigurator(state);
    const categories = r.values(pc.categories);
    const categoryPositions = r.zipObj(
        pc.sortedCategoryIds,
        r.map(Number, r.keys(pc.sortedCategoryIds))
    );

    return r.sortBy((c) => categoryPositions[c.id], categories);
}

export function variants(state) {
    const configurator = productConfigurator(state);
    const sortedVariantIds = r.propOr([], 'sortedVariantIds', configurator);
    const variantPositions = r.zipObj(
        sortedVariantIds,
        r.map(Number, r.keys(sortedVariantIds))
    );
    const variants = r.map((variant) => {
        return r.assoc(
            'selected',
            variant.id === configurator.selectedVariant,
            variant
        );
    }, r.values(configurator.variants));

    return r.sortBy((v) => variantPositions[v.id], variants);
}

export function urlState(state) {
    const pc = productConfigurator(state);

    return {
        spcids: r.pipe(
            r.prop('productConfigs'),
            r.filter(r.propSatisfies(r.lt(0), 'count')),
            r.map(r.prop('count'))
        )(pc),
        variant: pc.selectedVariant,
        namingProduct: isNamingProduct(state) || undefined,
    };
}

export function formFields(state) {
    const pc = productConfigurator(state);

    const productConfigFields = r.map((pc) => {
        return [
            `sylius_add_to_cart[cartItem][orderItemProductConfigs][${pc.id}][count]`,
            pc.count,
        ];
    }, r.values(pc.productConfigs));

    const customPizzaFields = r.ifElse(
        r.prop('customPizza'),
        () => [
            [
                'sylius_add_to_cart[cartItem][variant][custom-pizza]',
                r.path(['variants', r.prop('selectedVariant', pc), 'code'], pc),
            ],
        ],
        () => []
    )(pc);

    return r.concat(
        [
            [
                'sylius_add_to_cart[cartItem][productName]',
                r.path(['namedProduct', 'savedName'], pc),
            ],
        ],
        r.concat(productConfigFields, customPizzaFields)
    );
}

export function namedProduct(state) {
    return productConfigurator(state).namedProduct;
}

export function allowSave(state) {
    return productConfigurator(state).allowSave;
}

export function showVariantUpgrade(state) {
    return r.propOr(false, 'showVariantUpgrade', productConfigurator(state));
}

export function variantUpgradeDialogTexts(state) {
    return productConfigurator(state).variantUpgradeDialog;
}

export function showVariantDowngradeInvalid(state) {
    return r.propOr(
        false,
        'showVariantDowngradeInvalid',
        productConfigurator(state)
    );
}

export function variantDowngradeInvalidAlertTexts(state) {
    return productConfigurator(state).variantDowngradeInvalidAlert;
}

export function showConfigurationLimitExceeded(state) {
    return r.propOr(
        false,
        'showConfigurationLimitExceeded',
        productConfigurator(state)
    );
}

export function ConfigurationLimitExceededDialogTexts(state) {
    return productConfigurator(state).configurationLimitExceededDialog;
}

export function isNamingProduct(state) {
    return r.propOr(false, 'namingProduct', productConfigurator(state));
}

export function productConfiguratorSubmit(state) {
    return productConfigurator(state).productConfiguratorSubmit;
}
